import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Sector, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import api from "../services/api";
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Function to detect LaTeX patterns
const hasLatex = (text) => {
  if (!text || typeof text !== 'string') {
    console.log(`No LaTeX: Text is ${text}`);
    return false;
  }
  // More specific LaTeX patterns to avoid false positives with standalone underscores
  const latexPatterns = [
    /\\([a-zA-Z]+)(?:\s|{)/, // Matches LaTeX commands like \frac, \sqrt, etc.
    /\^{[^}]+}/, // Matches superscripts like ^{2}
    /_{[^}]+}/, // Matches subscripts like _{n}
    /\\\[.*?\\\]/, // Matches display math like \[...\]
    /\\\(.*?\\\)/, // Matches inline math like \(...\)
    /\$.*?\$/, // Matches math enclosed in single dollar signs
  ];
  const result = latexPatterns.some(pattern => pattern.test(text));
  console.log(`Checking LaTeX in: "${text}" -> ${result}`);
  return result;
};

const renderTextWithLatex = (text, isBlock = false) => {
  if (!text || typeof text !== 'string') return text || 'Not available';

  if (hasLatex(text)) {
    try {
      // Remove dollar signs for KaTeX rendering, but do not escape underscores
      let cleanedText = text.replace(/(\$\$|\$)/g, '');
      return isBlock ? (
        <BlockMath math={cleanedText} />
      ) : (
        <InlineMath math={cleanedText} />
      );
    } catch (error) {
      console.error(`Error rendering LaTeX for text "${text}":`, error);
      return text; // Fallback to plain text if LaTeX rendering fails
    }
  }
  // Return plain text without modification for underscores
  return text;
};

const StoredTestResultPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const resultRef = useRef(null);

  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const { studentName = 'Unknown' } = location.state || {};

  const isAdminAccess = location.state?.isAdmin || 
                       location.state?.userRole === 'admin' ||
                       localStorage.getItem('userRole') === 'admin';

  const sectionNameMap = {
    'Section 1': 'Blood Relation',
    'Section 2': 'Arithmetic',
    'Section 3': 'Logic',
    'Section 4': 'Verbal Reasoning',
  };

  const getGrade = (percentage) => {
    if (percentage >= 80) return 'Excellent';
    if (percentage >= 60) return 'Good';
    if (percentage >= 40) return 'Average';
    if (percentage < 40) return 'Needs Improvement';
    return 'Average';
  };

  const getScoreColor = (percentage) => {
    if (percentage < 40) return '#FF6666';
    if (percentage >= 40 && percentage <= 60) return '#FFA500';
    return '#4CAF50';
  };

  useEffect(() => {
    const fetchTestResult = async () => {
      try {
        setLoading(true);
        let apiUrl;
        if (isAdminAccess) {
          apiUrl = `/api/results/admin/test-result/${testId}`;
        } else {
          let studentId = localStorage.getItem('id');
          if (!studentId) {
            const studentData = localStorage.getItem('student');
            if (studentData) {
              try {
                const student = JSON.parse(studentData);
                studentId = student._id || student.id;
              } catch (parseError) {
                console.error('Error parsing student data:', parseError);
              }
            }
          }
          if (!studentId) {
            setError("Student ID not found. Please log in again.");
            setLoading(false);
            return;
          }
          apiUrl = `/api/results/test-result/${testId}?studentId=${studentId}`;
        }
        const response = await api.get(apiUrl);
        if (response.data) {
          setTestResult(response.data);
        } else {
          setError("Test result not found.");
        }
      } catch (err) {
        console.error('Error fetching test result:', err);
        if (err.response?.status === 404) {
          setError("Test result not found.");
        } else if (err.response?.status === 401) {
          setError("Authentication required. Please log in again.");
        } else if (err.response?.status === 400) {
          setError("Invalid request. Please check the test result ID.");
        } else {
          setError("Failed to fetch test result. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    if (testId) {
      fetchTestResult();
    } else {
      setError("No test ID provided.");
      setLoading(false);
    }
  }, [testId, location.state, isAdminAccess]);

  useEffect(() => {
    if (!loading && testResult) {
      const tableContainer = resultRef.current?.querySelector('.question-details-container');
      if (tableContainer) {
        tableContainer.style.display = 'none';
        tableContainer.offsetHeight;
        tableContainer.style.display = '';
      }
    }
  }, [loading, testResult]);

  const handleBackToDashboard = () => {
    if (isAdminAccess) {
      navigate('/dashboard');
    } else {
      navigate('/studentmark-dashboard');
    }
  };

  const handleDownloadResult = async () => {
    const element = resultRef.current;
    if (!element) return;

    const buttonsContainer = element.querySelector('.pdf-exclude-buttons');
    const originalButtonsDisplay = buttonsContainer ? buttonsContainer.style.display : null;
    if (buttonsContainer) {
      buttonsContainer.style.display = 'none';
    }

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: false,
        backgroundColor: null,
        width: element.scrollWidth,
        height: element.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector('[data-pdf-content]') || clonedDoc.body;
          if (clonedElement) {
            clonedElement.style.transform = 'scale(1)';
            clonedElement.style.transformOrigin = 'top left';
            const clonedButtons = clonedElement.querySelector('.pdf-exclude-buttons');
            if (clonedButtons) clonedButtons.remove();
            const questionTable = clonedElement.querySelector('.question-details-table');
            if (questionTable) {
              const thead = questionTable.querySelector('thead tr');
              if (thead) thead.removeChild(thead.querySelector('th:last-child')); // Remove "View" column header
              const tbody = questionTable.querySelector('tbody');
              if (tbody) {
                Array.from(tbody.querySelectorAll('tr')).forEach(row => {
                  row.removeChild(row.querySelector('td:last-child')); // Remove "View" column cells
                });
              }
            }
          }
        }
      });

      const pdfWidth = 595.28;
      const pdfHeight = 841.89;
      const margin = 20;
      const contentWidth = pdfWidth - (margin * 2);
      const contentHeight = pdfHeight - (margin * 2);
      const scale = contentWidth / canvas.width;
      const scaledHeight = canvas.height * scale;

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4'
      });

      if (scaledHeight <= contentHeight) {
        const imgData = canvas.toDataURL('image/png', 1.0);
        pdf.addImage(imgData, 'PNG', margin, margin, contentWidth, scaledHeight);
      } else {
        let yPosition = 0;
        let pageNumber = 1;
        while (yPosition < canvas.height) {
          const pageHeightInCanvas = contentHeight / scale;
          const remainingHeight = canvas.height - yPosition;
          let currentPageHeight = Math.min(pageHeightInCanvas, remainingHeight);
          if (remainingHeight > pageHeightInCanvas) currentPageHeight -= 50;
          currentPageHeight = Math.max(currentPageHeight, 100);

          const pageCanvas = document.createElement('canvas');
          const pageContext = pageCanvas.getContext('2d');
          pageCanvas.width = canvas.width;
          pageCanvas.height = currentPageHeight;
          pageContext.fillStyle = 'white';
          pageContext.fillRect(0, 0, canvas.width, currentPageHeight);
          pageContext.drawImage(canvas, 0, yPosition, canvas.width, currentPageHeight, 0, 0, canvas.width, currentPageHeight);

          const pageImgData = pageCanvas.toDataURL('image/png', 1.0);
          if (pageNumber > 1) pdf.addPage();
          pdf.addImage(pageImgData, 'PNG', margin, margin, contentWidth, currentPageHeight * scale);

          yPosition += currentPageHeight;
          pageNumber++;
          if (pageNumber > 50) {
            console.warn('Too many pages, stopping PDF generation');
            break;
          }
        }
      }

      const fileName = `${testResult.testName || 'Test_Result'}_${studentName || 'Student'}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      if (buttonsContainer && originalButtonsDisplay !== null) buttonsContainer.style.display = originalButtonsDisplay;
      else if (buttonsContainer) buttonsContainer.style.display = 'flex';
    }
  };

  const handleViewQuestion = (question, userAnswer, isCorrect, isAnswered) => {
    setSelectedQuestion({
      questionText: question.questionText,
      userAnswer: userAnswer || 'Not Answered',
      correctAnswer: question.correctAnswer,
      status: !isAnswered ? 'Skipped' : isCorrect ? 'Correct' : 'Wrong',
      originalIndex: question.originalIndex
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedQuestion(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)', fontFamily: "'Roboto', sans-serif" }}>
        <div style={{ fontSize: '18px', color: '#333', fontFamily: "'Roboto', sans-serif" }}>Loading test results...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)', fontFamily: "'Roboto', sans-serif" }}>
        <div style={{ fontSize: '18px', color: '#ff4d4f', marginBottom: '20px', fontFamily: "'Roboto', sans-serif" }}>{error}</div>
        <button onClick={handleBackToDashboard} style={backButtonStyle} className="glow-button">Back to Dashboard</button>
      </div>
    );
  }

  if (!testResult) return null;

  const { testName, overallPercentage, grade, totalQuestions, sectionWiseMarks = {}, questions = [], answers = [], totalAttempted, totalCorrect, totalIncorrect, notAttempted } = testResult;

  const pieData = Object.entries(sectionWiseMarks).map(([currentSection, data]) => ({
    name: sectionNameMap[currentSection] || currentSection,
    value: data.total ? parseFloat(((data.correct / data.total) * 100).toFixed(1)) : 0,
  }));

  const barData = [
    { name: 'Total Attempted', value: totalAttempted || 0 },
    { name: 'Not Attempted', value: notAttempted || 0 },
    { name: 'Total Correct', value: totalCorrect || 0 },
    { name: 'Total Incorrect', value: totalIncorrect || 0 },
  ];

  const COLORS = ['#66bfff', '#e6a6a6', '#8b9de6', '#9f87e6'];

  const onPieEnter = (_, index) => setActiveIndex(index);
  const onPieLeave = () => setActiveIndex(-1);

  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    return <Sector cx={cx} cy={cy} innerRadius={innerRadius} outerRadius={outerRadius + 20} startAngle={startAngle} endAngle={endAngle} fill={fill} cornerRadius={5} />;
  };

  const renderLabel = ({ value }) => `${value}%`;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '4px 6px', fontSize: '11px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', fontFamily: "'Roboto', sans-serif" }}>
          <p style={{ margin: 0, lineHeight: '1.1', fontFamily: "'Roboto', sans-serif" }}>{`${data.name}: ${['Total Attempted', 'Not Attempted', 'Total Correct', 'Total Incorrect'].includes(data.name) ? data.value : `${data.value}%`}`}</p>
        </div>
      );
    }
    return null;
  };

  const questionsBySection = questions.reduce((acc, q, index) => {
    const section = q.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push({ ...q, originalIndex: index });
    return acc;
  }, {});

  return (
    <div ref={resultRef} style={{ padding: '2rem 2rem 0 2rem', background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)', fontFamily: "'Roboto', sans-serif", height: 'auto', overflow: 'hidden', minHeight: '0' }}>
      <style>
        {`
          .glow-button:hover:not(:disabled) {
            background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
            font-family: 'Roboto', sans-serif;
          }
          @keyframes glow {
            0% { box-shadow: 0 0 5px rgba(255,187,187,0.8), 0 0 10px rgba(168,193,255,0.6), 0 0 15px rgba(197,168,255,0.4); }
            50% { box-shadow: 0 0 10px rgba(255,187,187,1), 0 0 20px rgba(168,193,255,0.8), 0 0 30px rgba(197,168,255,0.6); }
            100% { box-shadow: 0 0 5px rgba(255,187,187,0.8), 0 0 10px rgba(168,193,255,0.6), 0 0 15px rgba(197,168,255,0.4); }
          }
          .no-hover-bar .recharts-layer.recharts-bar-rectangle, .no-hover-bar .recharts-layer.recharts-bar-rectangle:hover, .no-hover-bar .recharts-layer.recharts-bar-rectangle:active, .no-hover-bar .recharts-layer.recharts-bar-rectangle:focus {
            fill-opacity: 1 !important; fill: inherit !important; background: none !important; cursor: default !important; pointer-events: none !important;
          }
          .no-hover-bar .recharts-bar-rectangles, .no-hover-bar .recharts-bar-rectangles:hover, .no-hover-bar .recharts-bar-rectangles:active, .no-hover-bar .recharts-bar-rectangles:focus {
            background: none !important; fill-opacity: 1 !important; pointer-events: none !important;
          }
          .no-hover-bar .recharts-layer.recharts-bar, .no-hover-bar .recharts-layer.recharts-bar:hover, .no-hover-bar .recharts-layer.recharts-bar:active, .no-hover-bar .recharts-layer.recharts-bar:focus {
            background: none !important; fill-opacity: 1 !important; pointer-events: none !important;
          }
          .no-hover-bar .recharts-wrapper, .no-hover-bar .recharts-wrapper:hover, .no-hover-bar .recharts-surface, .no-hover-bar .recharts-surface:hover {
            background: none !important; fill-opacity: 1 !important; pointer-events: auto !important;
          }
          .no-hover-bar .recharts-tooltip-wrapper, .no-hover-bar .recharts-tooltip-wrapper:hover { pointer-events: auto !important; }
          .question-details-table { width: 100%; max-width: 1600px; min-width: 1200px; border-collapse: collapse; background: #fff; border-radius: 10px; border: 2px solid transparent; background-image: linear-gradient(#fff,#fff), linear-gradient(to right,#FFBBBB,#A8C1FF,#C5A8FF); background-origin: border-box; background-clip: padding-box, border-box; margin: 0 auto; display: table; table-layout: fixed; font-family: 'Roboto', sans-serif; max-height: 550px; overflow-y: auto; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: thin; scrollbar-color: #6B21A8 #f1f1f1; }
          .question-details-table::-webkit-scrollbar { width: 10px; height: 10px; }
          .question-details-table::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 5px; }
          .question-details-table::-webkit-scrollbar-thumb { background: #6B21A8; border-radius: 5px; border: 2px solid #f1f1f1; }
          .question-details-table::-webkit-scrollbar-thumb:hover { background: #4a1575; }
          .question-details-table thead { position: sticky; top: 0; z-index: 1; background: linear-gradient(to right,#EFF6FF,#F3E8FF,#FEE7EF); font-family: 'Roboto', sans-serif; }
          .question-details-table thead th { padding: 10px; font-weight: bold; text-align: center; border: 1px solid #ddd; font-size: 13px; font-family: 'Roboto', sans-serif; color: #6B21A8; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
          .question-details-table thead th:nth-child(1) { width: 50%; }
          .question-details-table thead th:nth-child(2) { width: 12.5%; }
          .question-details-table thead th:nth-child(3) { width: 12.5%; }
          .question-details-table thead th:nth-child(4) { width: 12.5%; }
          .question-details-table thead th:nth-child(5) { width: 12.5%; }
          .question-details-table tbody { display: table-row-group; font-family: 'Roboto', sans-serif; }
          .question-details-table tr { display: table; width: 100%; table-layout: fixed; font-family: 'Roboto', sans-serif; }
          .question-details-table td, .question-details-table th { padding: 8px; border: 1px solid #ddd; font-size: 13px; font-family: 'Roboto', sans-serif; color: #333; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .question-details-table td:first-child { text-align: left; white-space: normal; word-wrap: break-word; width: 50%; }
          .katex { font-size: 1em !important; display: inline-flex !important; vertical-align: middle; font-family: 'Roboto', sans-serif !important; }
          .katex-display { margin: 0 !important; display: block !important; font-family: 'Roboto', sans-serif !important; }
          .section-table td:first-child, .section-table th:first-child { text-align: left !important; }
          .summary-table td:first-child, .summary-table th:first-child { text-align: left !important; }
          .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; font-family: 'Roboto', sans-serif; }
          .view-button {
            background: none;
            border: none;
            color: #cc8888;
            padding: 2px;
            cursor: pointer;
            transition: color 0.3s ease, transform 0.3s ease;
            font-family: 'Roboto', sans-serif;
          }
          .view-button:hover {
            color: #b36666 !important;
            transform: scale(1.1);
            font-family: 'Roboto', sans-serif !important;
          }
        `}
      </style>

      <div style={{ textAlign: 'center', marginBottom: '20px', fontFamily: "'Roboto', sans-serif" }}>
        <h1 style={{ color: '#333', fontSize: '30px', fontWeight: '0', fontFamily: "'Roboto', sans-serif", marginBottom: '10px' }}>{testName || 'Not Applicable'}</h1>
        {isAdminAccess && <h2 style={{ color: '#333', fontSize: '22px', fontWeight: '0', fontFamily: "'Roboto', sans-serif", marginBottom: '10px' }}>Name: {studentName}</h2>}
        <h2 style={{ color: '#333', fontFamily: "'Roboto', sans-serif" }}>
          Your Score <span style={{ color: getScoreColor(overallPercentage), fontFamily: "'Roboto', sans-serif" }}>{overallPercentage}%</span>{' '}
          <span style={{ color: getScoreColor(overallPercentage), fontFamily: "'Roboto', sans-serif" }}>{overallPercentage >= 40 ? 'Pass' : 'Fail'}</span>
        </h2>
        <p style={{ fontFamily: "'Roboto', sans-serif" }}>Questions Answered: {totalAttempted} / {totalQuestions}</p>
      </div>

      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', fontFamily: "'Roboto', sans-serif" }}>
        <div style={{ flex: '1', minWidth: '300px', maxWidth: '600px', fontFamily: "'Roboto', sans-serif" }}>
          <div className="charts-section">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart onMouseLeave={onPieLeave}>
                  <Pie
                    activeIndex={activeIndex}
                    activeShape={renderActiveShape}
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="40%"
                    cy="50%"
                    innerRadius="40%"
                    outerRadius="75%"
                    onMouseEnter={onPieEnter}
                    label={false}
                    labelLine={false}
                  >
                    {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    iconType="circle"
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    wrapperStyle={{ marginRight: '10px', maxWidth: '150px', fontFamily: "'Roboto', sans-serif" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : <p style={{ color: '#333', textAlign: 'center', fontFamily: "'Roboto', sans-serif" }}>No data available for Pie Chart</p>}
            <div style={{ marginTop: '20px', fontFamily: "'Roboto', sans-serif" }}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }} className="no-hover-bar">
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} tick={{ fontSize: '11px', fontFamily: "'Roboto', sans-serif" }} />
                  <YAxis tick={{ fontFamily: "'Roboto', sans-serif" }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#8884d8" barSize={20} isAnimationActive={false} cursor="default" onMouseEnter={() => {}} onMouseLeave={() => {}} onClick={() => {}} isInteractive={false}>
                    {barData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div style={{ flex: '1', minWidth: '100px', maxWidth: '500px', marginTop: '100px', marginBottom: '20px', marginLeft: '220px', fontFamily: "'Roboto', sans-serif" }}>
          <div style={{ maxHeight: '200px', overflowY: 'auto', fontFamily: "'Roboto', sans-serif" }}>
            <table className="section-table" style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '20px', backgroundImage: 'linear-gradient(#fff,#fff), linear-gradient(to right,#FFBBBB,#A8C1FF,#C5A8FF)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', border: '2px solid transparent', fontFamily: "'Roboto', sans-serif" }}>
              <thead><tr style={{ background: 'linear-gradient(to right,#EFF6FF,#F3E8FF,#FEE7EF)', color: '#6B21A8', fontFamily: "'Roboto', sans-serif" }}><th style={thStyle}>Section</th><th style={thStyle}>Total</th><th style={thStyle}>Correct</th><th style={thStyle}>%</th></tr></thead>
              <tbody>
                {Object.entries(sectionWiseMarks).length > 0 ? Object.entries(sectionWiseMarks).map(([currentSection, data], index) => {
                  const percentage = data.total ? ((data.correct / data.total) * 100).toFixed(1) : 0;
                  return <tr key={currentSection} style={{ background: index % 2 === 0 ? 'linear-gradient(to right,#FAFCFF,#FBF6FF,#FFF8FB)' : '#FFFFFF', fontFamily: "'Roboto', sans-serif" }}><td style={{ ...tdStyle, color: '#333' }}>{sectionNameMap[currentSection] || currentSection}</td><td style={{ ...tdStyle, color: '#333' }}>{data.total}</td><td style={{ ...tdStyle, color: '#333' }}>{data.correct}</td><td style={{ ...tdStyle, color: '#333' }}>{percentage}%</td></tr>;
                }) : <tr><td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: '#333' }}>No section-wise data available</td></tr>}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '60px', fontFamily: "'Roboto', sans-serif" }}>
            <table className="summary-table" style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '20px', overflow: 'hidden', backgroundImage: 'linear-gradient(#fff,#fff), linear-gradient(to right,#FFBBBB,#A8C1FF,#C5A8FF)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', border: '2px solid transparent', fontFamily: "'Roboto', sans-serif" }}>
              <thead><tr style={{ background: 'linear-gradient(to right,#EFF6FF,#F3E8FF,#FEE7EF)', color: '#6B21A8', fontFamily: "'Roboto', sans-serif" }}><th style={thStyle}>Metric</th><th style={thStyle}>Value</th></tr></thead>
              <tbody>
                <tr style={{ background: 'linear-gradient(to right,#FAFCFF,#FBF6FF,#FFF8FB)', fontFamily: "'Roboto', sans-serif" }}><td style={{ ...tdStyle, color: '#333' }}>Total Attempted</td><td style={{ ...tdStyle, color: '#333' }}>{totalAttempted}/{totalQuestions}</td></tr>
                <tr style={{ backgroundColor: '#FFFFFF', fontFamily: "'Roboto', sans-serif" }}><td style={{ ...tdStyle, color: '#333' }}>Not Attempted</td><td style={{ ...tdStyle, color: '#333' }}>{notAttempted}/{totalQuestions}</td></tr>
                <tr style={{ background: 'linear-gradient(to right,#FAFCFF,#FBF6FF,#FFF8FB)', fontFamily: "'Roboto', sans-serif" }}><td style={{ ...tdStyle, color: '#333' }}>Total Correct</td><td style={{ ...tdStyle, color: '#333' }}>{totalCorrect}/{totalQuestions}</td></tr>
                <tr style={{ backgroundColor: '#FFFFFF', fontFamily: "'Roboto', sans-serif" }}><td style={{ ...tdStyle, color: '#333' }}>Total Incorrect</td><td style={{ ...tdStyle, color: '#333' }}>{totalIncorrect}/{totalQuestions}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '2rem', fontFamily: "'Roboto', sans-serif" }}>
        <div style={legendBoxStyle}>Test Status: <span style={highlightBoxStyle}>Completed</span></div>
        <div style={legendBoxStyle}>Grade: <span style={highlightBoxStyle}>{grade || getGrade(overallPercentage)}</span></div>
      </div>

      <div style={{ marginTop: '30px', marginBottom: '20px', fontFamily: "'Roboto', sans-serif" }}>
        <h3 style={{ color: '#333', marginBottom: '10px', fontFamily: "'Roboto', sans-serif" }}>Question Details</h3>
        <div className="question-details-container">
          <table className="question-details-table">
            <thead><tr style={{ background: 'linear-gradient(to right,#EFF6FF,#F3E8FF,#FEE7EF)', color: '#6B21A8', fontFamily: "'Roboto', sans-serif" }}><th style={thStyle}>Questions</th><th style={thStyle}>Given Answer</th><th style={thStyle}>Correct Answer</th><th style={thStyle}>Status</th><th style={thStyle}>View</th></tr></thead>
            <tbody>
              {Object.entries(questionsBySection).length > 0 ? Object.entries(questionsBySection).map(([currentSection, sectionQuestions], sectionIndex) => (
                <React.Fragment key={`section-${currentSection}`}>
                  <tr style={{ backgroundColor: '#e0e7ff', fontWeight: 'bold', borderTop: sectionIndex > 0 ? '2px solid #EFF6FF' : 'none', fontFamily: "'Roboto', sans-serif" }}>
                    <td colSpan="5" style={{ ...tdStyle, color: '#333' }}>{sectionNameMap[currentSection] || currentSection}</td>
                  </tr>
                  {sectionQuestions.map((q, idx) => {
                    const userAnswer = answers[q.originalIndex];
                    const isCorrect = userAnswer === q.correctAnswer;
                    const isAnswered = userAnswer !== undefined && userAnswer !== null && userAnswer !== '';
                    const status = !isAnswered ? 'Skipped' : isCorrect ? 'Correct' : 'Wrong';
                    return (
                      <tr key={q.originalIndex} style={{ background: idx % 2 === 0 ? 'linear-gradient(to right,#FAFCFF,#FBF6FF,#FFF8FB)' : '#FFFFFF', fontFamily: "'Roboto', sans-serif" }}>
                        <td style={{ ...tdStyle, color: '#333', textAlign: 'left' }}>Q{q.originalIndex + 1}. {renderTextWithLatex(q.questionText, false)}</td>
                        <td style={{ ...tdStyle, color: '#333', textAlign: 'center' }}>{renderTextWithLatex(userAnswer || 'Not Answered', false)}</td>
                        <td style={{ ...tdStyle, color: '#333', textAlign: 'center' }}>{renderTextWithLatex(q.correctAnswer, false)}</td>
                        <td style={{ ...tdStyle, color: status === 'Correct' ? '#4CAF50' : status === 'Wrong' ? '#FF6666' : '#FFA500', textAlign: 'center' }}>{status}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <button className="view-button" onClick={() => handleViewQuestion(q, userAnswer, isCorrect, isAnswered)} title="View" aria-label="View question">
                            <VisibilityIcon style={{ fontSize: '17px', color: '#cc8888', border: 'none' }} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              )) : <tr><td colSpan="5" style={{ ...tdStyle, textAlign: 'center', color: '#333' }}>No questions available</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && selectedQuestion && (
        <div className="modal-overlay">
          <div style={viewModal}>
            <h3 style={viewModalHeader}>Question Details</h3>
            <div style={viewModalContent}>
              <div style={viewModalSection}>
                <div style={viewModalLabel}>Question {selectedQuestion.originalIndex + 1}</div>
                <div style={{ ...viewModalValue, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderTextWithLatex(selectedQuestion.questionText, true)}</div>
              </div>
              <div style={viewModalSection}>
                <div style={viewModalLabel}>Your Answer</div>
                <div style={{ ...viewModalValue, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderTextWithLatex(selectedQuestion.userAnswer, true)}</div>
              </div>
              <div style={viewModalSection}>
                <div style={viewModalLabel}>Correct Answer</div>
                <div style={{ ...viewModalValue, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderTextWithLatex(selectedQuestion.correctAnswer, true)}</div>
              </div>
              <div style={viewModalSection}>
                <div style={viewModalLabel}>Status</div>
                <div style={viewModalValue}>
                  <span style={{ color: selectedQuestion.status === 'Correct' ? '#4CAF50' : selectedQuestion.status === 'Wrong' ? '#FF6666' : '#FFA500', fontWeight: 'bold' }}>
                    {selectedQuestion.status}
                  </span>
                </div>
              </div>
            </div>
            <button style={viewModalCloseButton} onClick={handleCloseModal}>Close</button>
          </div>
        </div>
      )}

      <div className="pdf-exclude-buttons" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginTop: '2rem', marginBottom: '2rem', width: '100%', fontFamily: "'Roboto', sans-serif" }}>
        <button onClick={handleBackToDashboard} style={backButtonStyle} className="glow-button">Back to Dashboard</button>
        <button onClick={handleDownloadResult} style={backButtonStyle} className="glow-button">Download Result</button>
      </div>
    </div>
  );
};

const tdStyle = { padding: '8px', border: '1px solid #ddd', fontSize: '12px', fontFamily: "'Roboto', sans-serif", textAlign: 'center' };
const thStyle = { padding: '10px', fontWeight: 'bold', textAlign: 'center', border: '1px solid #ddd', fontSize: '14px', fontFamily: "'Roboto', sans-serif" };
const legendBoxStyle = { fontSize: '16px', fontFamily: "'Roboto', sans-serif" };
const highlightBoxStyle = { display: 'inline-block', backgroundColor: '#4CAF50', color: 'white', padding: '2px 10px', borderRadius: '5px', marginLeft: '6px', fontFamily: "'Roboto', sans-serif" };
const backButtonStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '6px 16px', fontSize: '14px', fontFamily: "'Roboto', sans-serif", fontWeight: '500', color: '#6B21A8', textTransform: 'uppercase', letterSpacing: '1px', cursor: 'pointer', borderRadius: '30px', border: '2px solid transparent', backgroundImage: 'linear-gradient(#ffffff,#ffffff), linear-gradient(to right,#FFBBBB,#A8C1FF,#C5A8FF)', backgroundOrigin: 'border-box', backgroundClip: 'padding-box, border-box', transition: 'all 0.4s ease' };
const viewModal = {
  backgroundColor: 'white',
  borderRadius: '0.5rem',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  color: '#1F2937',
  fontFamily: "'Roboto', sans-serif",
  width: '90vw',
  maxWidth: '800px',
  margin: '0 auto',
  padding: '24px',
  maxHeight: '81vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  position: 'relative',
};
const viewModalHeader = { 
  fontSize: '20px', 
  fontWeight: '600', 
  color: '#6B21A8',
  marginBottom: '16px',
  borderBottom: '1px solid #e5e7eb',
  paddingBottom: '8px',
  fontFamily: "'Roboto', sans-serif",
};
const viewModalContent = { 
  fontSize: '14px', 
  color: '#1F2937', 
  lineHeight: '1.6',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  fontFamily: "'Roboto', sans-serif",
};
const viewModalSection = {
  paddingBottom: '12px',
  borderBottom: '1px solid #e5e7eb',
  fontFamily: "'Roboto', sans-serif",
};
const viewModalLabel = { 
  fontWeight: '600', 
  color: '#6B21A8',
  marginBottom: '4px',
  fontFamily: "'Roboto', sans-serif",
};
const viewModalValue = {
  color: '#1F2937',
  fontFamily: "'Roboto', sans-serif",
};
const viewModalCloseButton = { 
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '5px 15px',
  fontSize: '14px',
  fontWeight: '500',
  color: '#6B21A8',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  cursor: 'pointer',
  borderRadius: '30px',
  border: '2px solid transparent',
  backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
  transition: 'all 0.4s ease',
  alignSelf: 'flex-end',
  marginTop: '16px',
  fontFamily: "'Roboto', sans-serif",
};

export default StoredTestResultPage;